import Cookies from "js-cookie";
import { useCallback, useEffect, useRef, useState } from "react";

// E2E tests (in a separate repository) set this cookie to a secret value to bypass Turnstile.
// The backend validates the secret and skips Turnstile validation; here we only check for the
// cookie's presence so the form submits without waiting for a token. The empty token we send is
// harmless because the server ignores it when the bypass is active.
const AUTOMATED_TESTING_COOKIE = "yoast-automated-testing";
const isAutomatedTesting = () => Boolean( Cookies.get( AUTOMATED_TESTING_COOKIE ) );

/**
 * Manages Turnstile token state and queues form submissions that fire
 * before the widget has produced a token.
 *
 * The payload is snapshotted at the moment requestSubmit() is called,
 * so further field edits while the submit is queued do not change what
 * is sent.
 *
 * If the Turnstile script itself fails to load, submissions fall back to firing with an
 * empty token so the server can produce a visible error rather than the form hanging.
 *
 * @param {object} options The hook options.
 * @param {function} options.onSubmit Called as onSubmit( payload, token ) once a token is
 *   available — immediately if already verified, otherwise when the widget initializes.
 *   Called with an empty token if the Turnstile script could not be loaded.
 * @param {*} options.error External submission error; when it becomes truthy the widget
 *   is reset and any queued submit is dropped.
 *
 * @returns {object} Turnstile state, callbacks, ref, and the requestSubmit function.
 */
export const useTurnstileSubmit = ( { onSubmit, error } ) => {
	const [ token, setToken ] = useState( "" );
	const [ hasError, setHasError ] = useState( false );
	const [ isPending, setIsPending ] = useState( false );
	// When the Turnstile script itself fails to load (e.g. challenges.cloudflare.com blocked),
	// the widget's error/timeout callbacks never fire. In that case we fall back to submitting
	// without a token and let the server reject — a visible error beats a hung form.
	const [ scriptLoadFailed, setScriptLoadFailed ] = useState( false );

	const turnstileRef = useRef( null );
	const pendingPayloadRef = useRef( null );
	const onSubmitRef = useRef( onSubmit );
	// Sync during render so the flush effect always sees the latest onSubmit
	// without taking it as a dependency.
	onSubmitRef.current = onSubmit;

	// Tokens are single-use; reset the widget so a fresh token is issued for the next submit.
	const consumeToken = useCallback( () => {
		setToken( "" );
		if ( turnstileRef.current ) {
			turnstileRef.current.reset();
		}
	}, [] );

	useEffect( () => {
		if ( ! error ) {
			return;
		}
		if ( turnstileRef.current ) {
			turnstileRef.current.reset();
		}
		setToken( "" );
		setIsPending( false );
		pendingPayloadRef.current = null;
	}, [ error ] );

	useEffect( () => {
		if ( ! isPending || hasError ) {
			return;
		}
		if ( ! token && ! scriptLoadFailed ) {
			return;
		}
		const payload = pendingPayloadRef.current;
		const flushedToken = token;
		pendingPayloadRef.current = null;
		setIsPending( false );
		if ( token ) {
			consumeToken();
		}
		if ( payload !== null ) {
			onSubmitRef.current( payload, flushedToken );
		}
	}, [ isPending, token, hasError, scriptLoadFailed, consumeToken ] );

	const onTurnstileVerify = useCallback( ( nextToken ) => {
		setToken( nextToken );
		setHasError( false );
	}, [] );

	const onTurnstileError = useCallback( () => {
		setToken( "" );
		setHasError( true );
		setIsPending( false );
		pendingPayloadRef.current = null;
	}, [] );

	const onTurnstileTimeout = useCallback( () => {
		setToken( "" );
		setHasError( true );
		setIsPending( false );
		pendingPayloadRef.current = null;
	}, [] );

	const onTurnstileExpire = useCallback( () => {
		setToken( "" );
		setIsPending( false );
		pendingPayloadRef.current = null;
	}, [] );

	const onTurnstileScriptError = useCallback( () => {
		setScriptLoadFailed( true );
	}, [] );

	const requestSubmit = useCallback( ( payload ) => {
		// Automated tests bypass Turnstile entirely; submit immediately with an empty token.
		if ( isAutomatedTesting() ) {
			onSubmitRef.current( payload, "" );
			return;
		}
		if ( hasError ) {
			return;
		}
		if ( token ) {
			const consumed = token;
			consumeToken();
			onSubmitRef.current( payload, consumed );
			return;
		}
		if ( scriptLoadFailed ) {
			onSubmitRef.current( payload, "" );
			return;
		}
		pendingPayloadRef.current = payload;
		setIsPending( true );
	}, [ hasError, token, scriptLoadFailed, consumeToken ] );

	return {
		turnstileRef,
		turnstileBypassed: isAutomatedTesting(),
		turnstileVerified: !! token,
		turnstileError: hasError,
		turnstilePending: isPending,
		onTurnstileVerify,
		onTurnstileExpire,
		onTurnstileError,
		onTurnstileTimeout,
		onTurnstileScriptError,
		requestSubmit,
	};
};
